import Box from '@mui/material/Box';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import type { SelectChangeEvent } from '@mui/material/Select';
import { signupCopy } from '@/Pages/Signup/copy';
import { SignupDocumentField } from '@/Pages/Signup/components/SignupDocumentField';
import { SignupFormField } from '@/Pages/Signup/components/SignupFormField';
import { COUNTRIES } from '@/Pages/Signup/utils/countries';
import type { SignupFormData } from '@/types/signup';
import '@/Pages/Signup/SignupPage.sass';

type SignupFormProps = {
  form: SignupFormData;
  fieldErrors: Partial<Record<keyof SignupFormData, string>>;
  touched: Partial<Record<keyof SignupFormData, boolean>>;
  setField: (field: keyof SignupFormData, value: string) => void;
  blurField: (field: keyof SignupFormData) => void;
};

export function SignupForm({
  form,
  fieldErrors,
  touched,
  setField,
  blurField,
}: SignupFormProps): JSX.Element {
  const nationalityError = touched.nationality ? fieldErrors.nationality : undefined;
  const hasNationalityError = Boolean(nationalityError);
  const nationalityErrorId = 'nationality-error';

  const handleNationalityChange = (event: SelectChangeEvent<string>) => {
    setField('nationality', event.target.value);
  };

  return (
    <Box
      component="form"
      className="signup-form"
      data-testid="signup-form"
      noValidate
      onSubmit={(event) => {
        event.preventDefault();
      }}
    >
      <SignupFormField
        id="fullName"
        label={signupCopy.fullNameLabel}
        placeholder={signupCopy.fullNamePlaceholder}
        value={form.fullName}
        error={fieldErrors.fullName}
        showError={Boolean(touched.fullName)}
        onChange={(value) => setField('fullName', value)}
        onBlur={() => blurField('fullName')}
        autoComplete="name"
      />

      <SignupFormField
        id="email"
        label={signupCopy.emailLabel}
        placeholder={signupCopy.emailPlaceholder}
        type="email"
        value={form.email}
        error={fieldErrors.email}
        showError={Boolean(touched.email)}
        onChange={(value) => setField('email', value)}
        onBlur={() => blurField('email')}
        inputMode="email"
        autoComplete="email"
      />

      <SignupFormField
        id="birthDate"
        label={signupCopy.birthDateLabel}
        type="date"
        value={form.birthDate}
        error={fieldErrors.birthDate}
        showError={Boolean(touched.birthDate)}
        onChange={(value) => setField('birthDate', value)}
        onBlur={() => blurField('birthDate')}
        autoComplete="bday"
      />

      <FormControl
        fullWidth
        margin="normal"
        error={hasNationalityError}
        className="signup-form__nationality"
        data-testid="signup-field-nationality"
      >
        <InputLabel id="nationality-label">{signupCopy.nationalityLabel}</InputLabel>
        <Select
          labelId="nationality-label"
          id="nationality"
          name="nationality"
          value={form.nationality}
          label={signupCopy.nationalityLabel}
          onChange={handleNationalityChange}
          onBlur={() => blurField('nationality')}
          inputProps={{
            'aria-invalid': hasNationalityError,
            'aria-describedby': hasNationalityError ? nationalityErrorId : undefined,
          }}
        >
          {COUNTRIES.map((country) => (
            <MenuItem key={country.code} value={country.code}>
              {country.label}
            </MenuItem>
          ))}
        </Select>
        {hasNationalityError ? (
          <FormHelperText id={nationalityErrorId} role="alert" data-testid="nationality-error">
            {nationalityError}
          </FormHelperText>
        ) : null}
      </FormControl>

      <SignupDocumentField
        nationality={form.nationality}
        value={form.document}
        error={fieldErrors.document}
        showError={Boolean(touched.document)}
        onChange={(value) => setField('document', value)}
        onBlur={() => blurField('document')}
      />
    </Box>
  );
}
